import { AddressProps, Any } from '@app/types'
import axios from 'axios'
import fileDownload from 'js-file-download'
import { sampleSize, startCase, toLower } from 'lodash'
import { v4 as uuidv4 } from 'uuid'

export const isValidPhoneNumber = (number: string) => {
  if (number && number.length > 0) {
    return /[+]1[2-9]{1}[0-9]{9}/.test(number.replace(/\(|\)|\s|\\-/g, ''))
  } else {
    return false
  }
}

export const isValidEmail = (email: string) => {
  return /^(([^<>()\\[\]\\.,;:\s@"]+(\.[^<>()\\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
    email.toLowerCase(),
  )
}

export const capitalizeFirstLetter = (text: string) => {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

export const stripHTMLTags = (text: string): string => {
  return text.replace(/(<([^>]+)>)|(&[a-z]*;)/gi, '')
}

export const toCurrency = (amount: number, sign = true) => {
  const formatter = new Intl.NumberFormat('en-US', {
    currency: 'USD',
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    ...(sign && { style: 'currency' }),
  })

  return formatter.format(amount).replace(/^(\D+)/, '$1 ')
}

export const toBigCurrency = (amount: number, sign = true) => {
  const formatter = new Intl.NumberFormat('en-US', {
    currency: 'USD',
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
    ...(sign && { style: 'currency' }),
  })

  return formatter.format(amount).replace(/^(\D+)/, '$1 ')
}

export const toSmallerCurrency = (amount: number, sign = true) => {
  const formatter = new Intl.NumberFormat('en-US', {
    currency: 'USD',
    maximumFractionDigits: 7,
    minimumFractionDigits: 2,
    ...(sign && { style: 'currency' }),
  })

  return formatter.format(amount).replace(/^(\D+)/, '$1 ')
}

export const youtubeToEmbeddedUrl = (url: string) => {
  const getId = (url: string) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
    const match = url.match(regExp)

    return match && match[2].length === 11 ? match[2] : null
  }
  const videoId = getId(url)
  return `https://www.youtube.com/embed/${videoId}`
}

export const openInNewTab = (url: string) => {
  const newWindow = window.open(url, '_blank', 'noopener,noreferrer')
  if (newWindow) newWindow.opener = null
}

export const formatEnumToString = (enumString: string) => {
  if (typeof enumString === 'string' || (enumString as any) instanceof String)
    return enumString
      .replace(/_/g, ' ')
      .replace(/AND/g, '&')
      .replace(/\w\S*/g, (word) => {
        return word.charAt(0).toUpperCase() + word.substr(1).toLowerCase()
      })
  else return ''
}

export const formatStringToEnum = (string: string) => {
  if (string) {
    return string.trim().replace(/ /g, '_').replace(/&/g, 'AND').toLocaleUpperCase()
  }
}

export const getRandomUUID = () => {
  return uuidv4()
}

export const downloadFile = (url: string, filename: string) => {
  axios
    .get(url, {
      responseType: 'blob',
    })
    .then((res) => {
      fileDownload(res.data, filename)
    })
    .catch(() => null)
}

export const copyTextToClipBoard = (text: string) => {
  navigator.clipboard.writeText(text)
}

export const toTitleCase = (text: string) => {
  return startCase(toLower(text))
}

export const addressToString = (address: AddressProps) => {
  if (!address || !Object.keys(address).length) return 'N/A'
  return `${address.lineAddress1}${address.city}${address.country}${address.state}${address.zipCode}`
}

export const getEllipsesText = (str: string, n: number) => {
  return str.length > n ? str.slice(0, n - 1) + '&hellip;' : str
}

export const convertToRoman = (num: number) => {
  const numeralCodes = [
    ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX'], // Ones
    ['', 'X', 'XX', 'XXX', 'XL', 'L', 'LX', 'LXX', 'LXXX', 'XC'], // Tens
    ['', 'C', 'CC', 'CCC', 'CD', 'D', 'DC', 'DCC', 'DCCC', 'CM'], // Hundred
  ]
  let numeral = ''
  const digits = num.toString().split('').reverse()
  for (let i = 0; i < digits.length; i++) {
    numeral = numeralCodes[i][parseInt(digits[i])] + numeral
  }
  return numeral
}

// Deep copies an object or an array
export const deepCopy = (value: Any) => {
  const isArray = Array.isArray(value)
  if (isArray) {
    const deepCopiedObject = JSON.parse(JSON.stringify({ key: value }))
    return deepCopiedObject.key
  } else return JSON.parse(JSON.stringify(value))
}

/**
 * The function formats a wallet address by replacing the middle characters with ellipses if the
 * address is longer than 8 characters.
 * @param {string} str - a string representing a wallet address that needs to be formatted.
 * @returns The function `formatWalletAddress` returns a formatted string that shows the first four
 * characters of the input string, followed by three dots, and then the last four characters of the
 * input string. If the input string has eight or fewer characters, the function returns the original
 * string.
 */
export const formatWalletAddress = (str: string) => {
  if (!str) return undefined
  if (str.length <= 8) {
    return str // Return the original string if it has 8 or fewer characters
  } else {
    const firstFourChars = str.slice(0, 4) // Get the first 4 characters
    const lastFourChars = str.slice(-4) // Get the last 4 characters
    return `${firstFourChars}...${lastFourChars}` // Join them with "..." separator
  }
}

export const getTagColor = (value: string) => {
  value = value.toLocaleLowerCase()

  const SUCCESS = ['confirmed']
  const FAILED = ['failed']

  if (SUCCESS.includes(value)) return 'success'
  if (FAILED.includes(value)) return 'error'

  return 'default'
}

/**
 * This TypeScript function checks if a given string is a valid number.
 */
export const isNumber = (str: string): boolean => {
  if (typeof str !== 'string') {
    return false
  }
  return !isNaN(parseFloat(str)) && isFinite(Number(str))
}

/**
 * This function updates the URL query parameter "tab" with a new value and replaces the current
 * browser history state with the updated URL.
 */
export const persistTabValue = (value: string) => {
  const newQueryParam = `tab=${value}`
  const newUrl = `${window.location.pathname}?${newQueryParam}`
  window.history.replaceState(null, null, newUrl)
}

/**
 * This function generates a random number with a specified number of digits.
 */
export const generateRandomNumber = (numDigits = 6) => {
  const digits = sampleSize([0, 1, 2, 3, 4, 5, 6, 7, 8, 9], numDigits)
  return Number(digits.join(''))
}
